import type { Pt, RoomConfig } from './types.js';

/**
 * チップ単位と、金額の妥当性の判定（要件6.13、12.9）。
 *
 * 判定はすべてここに置く。サーバは操作の検証に、クライアントはボタンを押せるかどうかの
 * 判定に、同じ関数を使う。2箇所に書くと、クライアントでは押せるのにサーバで弾かれる額
 * （またはその逆）が生まれる。
 *
 * 返す値は「問題があればその説明、無ければ null」。クライアントはこの文言をそのまま
 * 入力欄の下に出す。
 */

/** 単位の上限。桁の打ち間違いによる事故を防ぐ。 */
export const MAX_CHIP_UNIT = 1_000_000;

/** 設定されたチップ単位。導入前に作られたルームは単位1として扱う。 */
export function chipUnitOf(config: RoomConfig): Pt {
  const unit = config.chipUnit;
  return unit !== undefined && Number.isInteger(unit) && unit > 0 ? unit : 1;
}

export function isMultipleOf(amount: Pt, unit: Pt): boolean {
  return Number.isInteger(amount) && amount % unit === 0;
}

/** 単位の倍数へ切り下げる。 */
export function floorToUnit(amount: Pt, unit: Pt): Pt {
  return Math.floor(amount / unit) * unit;
}

/** 単位の倍数へ切り上げる。 */
export function ceilToUnit(amount: Pt, unit: Pt): Pt {
  return Math.ceil(amount / unit) * unit;
}

function notMultiple(unit: Pt): string {
  return `${unit.toLocaleString('en-US')} の倍数で入力してください`;
}

/** ルーム作成時の設定の妥当性。 */
export function roomConfigProblem(config: Required<RoomConfig>): string | null {
  const { smallBlind, bigBlind, defaultBuyIn, chipUnit } = config;
  const positive = [smallBlind, bigBlind, defaultBuyIn, chipUnit].every(
    (v) => Number.isInteger(v) && v > 0,
  );
  if (!positive) return 'ブラインド・バイイン・チップ単位は正の整数で入力してください';
  if (chipUnit > MAX_CHIP_UNIT) return 'チップ単位が大きすぎます';
  if (smallBlind > bigBlind) return 'スモールブラインドはビッグブラインド以下にしてください';
  if (defaultBuyIn < bigBlind) return 'デフォルトバイインはビッグブラインド以上にしてください';
  if (!isMultipleOf(smallBlind, chipUnit) || !isMultipleOf(bigBlind, chipUnit)) {
    return 'ブラインドはチップ単位の倍数にしてください';
  }
  if (!isMultipleOf(defaultBuyIn, chipUnit)) {
    return 'デフォルトバイインはチップ単位の倍数にしてください';
  }
  return null;
}

/** 着席時のバイイン額の妥当性。 */
export function buyInProblem(config: RoomConfig, buyIn: Pt): string | null {
  if (!Number.isInteger(buyIn) || buyIn <= 0) return 'バイイン額は正の整数で入力してください';
  const unit = chipUnitOf(config);
  if (!isMultipleOf(buyIn, unit)) return notMultiple(unit);
  return null;
}

/** スタック調整の増減額の妥当性。負になるかどうかは席の状態に依るので呼び出し側で見る。 */
export function adjustDeltaProblem(config: RoomConfig, delta: Pt): string | null {
  if (!Number.isInteger(delta) || delta === 0) return '増減額は0以外の整数で入力してください';
  const unit = chipUnitOf(config);
  if (!isMultipleOf(delta, unit)) return notMultiple(unit);
  return null;
}

export type SizedSpec = { minTo: Pt; maxTo: Pt; step: Pt };

/**
 * ベット / レイズの到達額 `to` の妥当性。
 *
 * 全額（`maxTo`）だけは単位の倍数でなくてよい。単位の倍数にならないスタックを持つ席
 * （単位の導入前から座っている席など）が、全額を出せなくなるのを避ける。
 */
export function sizedAmountProblem(spec: SizedSpec, to: Pt): string | null {
  if (!Number.isInteger(to)) return '整数で入力してください';
  if (to < spec.minTo || to > spec.maxTo) {
    return `${spec.minTo.toLocaleString('en-US')} 〜 ${spec.maxTo.toLocaleString('en-US')} の範囲で入力してください`;
  }
  if (to !== spec.maxTo && !isMultipleOf(to, spec.step)) return notMultiple(spec.step);
  return null;
}

export function isValidSizedAmount(spec: SizedSpec, to: Pt): boolean {
  return sizedAmountProblem(spec, to) === null;
}
